import React, { createContext, useContext, useReducer, useEffect } from 'react';

const FormContext = createContext();

const initialState = {
  currentStep: 1,
  formData: {
    step1: {
      businessName: '',
      contactName: '',
      email: '',
      phone: '',
      industry: '',
      businessDescription: '',
      websiteType: '',
      currentWebsiteUrl: '',
      improvementNeeds: ''
    },
    step2: {
      primaryColour: '',
      secondaryColour: '',
      colourComments: '',
      selectedFont: '',
      typographyComments: '',
      designStyle: [],
      inspirationUrls: '',
      designReferences: ''
    },
    step3: {
      logoStatus: '',
      logoFiles: [],
      logoIdeas: '',
      logoStyles: [],
      currentLogoFiles: [],
      logoChanges: '',
      brandAssets: [],
      brandAssetFiles: []
    },
    step4: {
      selectedPages: [],
      pageContent: {},
      imagePreferences: ''
    },
    step5: {
      primaryAgeRange: [],
      geographicFocus: [],
      localRegion: '',
      customerType: '',
      otherCustomerType: ''
    },
    step6: {
      domainOwnership: '',
      currentDomain: '',
      preferredDomain: '',
      hostingChoice: '',
      emailHosting: '',
      socialMedia: {
        facebook: '',
        instagram: '',
        twitter: '',
        linkedin: '',
        youtube: '',
        tiktok: '',
        whatsapp: '',
        other: ''
      }
    },
    step7: {
      termsAccepted: false,
      confirmationAccepted: false
    }
  },
  errors: {},
  isSubmitting: false
};

function formReducer(state, action) {
  switch (action.type) {
    case 'SET_CURRENT_STEP':
      return { ...state, currentStep: action.payload };
    case 'UPDATE_FORM_DATA':
      const updatedFormData = {
        ...state.formData,
        [action.step]: {
          ...state.formData[action.step],
          ...action.payload
        }
      };
      // Save to localStorage
      localStorage.setItem('websiteFormData', JSON.stringify(updatedFormData));
      return { ...state, formData: updatedFormData };
    case 'SET_ERRORS':
      return { ...state, errors: action.payload };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.payload };
    case 'RESET_FORM':
      localStorage.removeItem('websiteFormData');
      return { ...initialState };
    case 'LOAD_FROM_STORAGE':
      return { ...state, formData: action.payload };
    default:
      return state;
  }
}

export function FormProvider({ children }) {
  const [state, dispatch] = useReducer(formReducer, initialState);

  useEffect(() => {
    // Load form data from localStorage on mount
    const savedData = localStorage.getItem('websiteFormData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        dispatch({ type: 'LOAD_FROM_STORAGE', payload: parsedData });
      } catch (error) {
        console.error('Error loading form data:', error);
      }
    }
  }, []);

  const updateFormData = (step, data) => {
    dispatch({ type: 'UPDATE_FORM_DATA', step, payload: data });
  };

  const setCurrentStep = (step) => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: step });
  };

  const setErrors = (errors) => {
    dispatch({ type: 'SET_ERRORS', payload: errors });
  };

  const setSubmitting = (isSubmitting) => {
    dispatch({ type: 'SET_SUBMITTING', payload: isSubmitting });
  };

  const resetForm = () => {
    dispatch({ type: 'RESET_FORM' });
  };

  const validateStep = (stepNumber) => {
    const stepData = state.formData[`step${stepNumber}`];
    const stepErrors = {};

    switch (stepNumber) {
      case 1:
        if (!stepData.businessName) stepErrors.businessName = 'Business name is required';
        if (!stepData.contactName) stepErrors.contactName = 'Contact name is required';
        if (!stepData.email) stepErrors.email = 'Email is required';
        // if (!stepData.industry) stepErrors.industry = 'Industry is required';
        break;
      // case 5:
      //   if (!stepData.customerType) stepErrors.customerType = 'Customer type is required';
      //   break;
      // case 6:
      //   if (!stepData.domainOwnership) stepErrors.domainOwnership = 'Domain ownership status is required';
      //   break;
      case 7:
        if (!stepData.termsAccepted) stepErrors.termsAccepted = 'Terms and conditions must be accepted';
        if (!stepData.confirmationAccepted) stepErrors.confirmationAccepted = 'Information confirmation is required';
        break;
    }

    return stepErrors;
  };

  const getProgress = () => {
  let totalFields = 0;
  let filledFields = 0;

  const isFilled = (value) => {
    if (Array.isArray(value)) return value.some(isFilled);
    if (typeof value === 'object' && value !== null) {
      return Object.values(value).some(isFilled);
    }
    // Only count as filled if it's a non-empty string or a true value
    return typeof value === 'string' ? value.trim() !== '' : Boolean(value);
  };

  Object.entries(state.formData).forEach(([stepKey, stepData]) => {
    Object.entries(stepData).forEach(([key, value]) => {
      // Exclude non-user-input or system fields
      if (
        key.toLowerCase().includes('hidden') ||
        key.toLowerCase().includes('auto') ||
        key.toLowerCase().includes('internal') ||
        key.toLowerCase().includes('extract')
      ) {
        return;
      }

      // If it's a nested object (like social media), count only if any field is relevant
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const entries = Object.entries(value);
        entries.forEach(([subKey, subValue]) => {
          totalFields++;
          if (isFilled(subValue)) filledFields++;
        });
      } else {
        totalFields++;
        if (isFilled(value)) filledFields++;
      }
    });
  });

  const percentage =
    totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;

  // Prevent small rounding noise — only show >0 once user actually fills something
  return filledFields === 0 ? 0 : percentage;
};

  return (
    <FormContext.Provider value={{
      ...state,
      updateFormData,
      setCurrentStep,
      setErrors,
      setSubmitting,
      resetForm,
      validateStep,
      getProgress
    }}>
      {children}
    </FormContext.Provider>
  );
}

export const useForm = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useForm must be used within a FormProvider');
  }
  return context;
};